/**
 * Business-role → intended landing-route lookup.
 *
 * Most of the 7 role-specific dashboards the Login page design references
 * aren't built yet — each is its own, already-scoped `implement_use_case`
 * activity the Specialist dispatches later (see concern
 * 45c3d3cf-d7c3-4c48-ab2e-fc6140d65aca). Every not-yet-built entry's `path`
 * is `/` (the generic home page) today; `useCaseId` records the target use
 * case so a future activity that builds one of these pages only has to:
 *   1. add its route in frontend/src/App.jsx, and
 *   2. flip that one entry's `path` here to the real route.
 * The login redirect logic (frontend/src/pages/login/AuthForm.jsx) never
 * needs to change again.
 *
 * 'Operator Admin' is flipped to /tenant-cafeteria-management by
 * UC 70c551f3-e63f-4af9-bd58-36f9c41115c7 ("CAF-01: Set up tenant,
 * cafeterias, users and role scoping") — that page IS the Operator Admin
 * dashboard. `useCaseId` still records the separate, not-yet-built full
 * RBAC/user-management use case (9a125829-...), which the "Assign to
 * Cafeteria" control on this page is only a minimal stand-in for.
 *
 * 'Finance Admin' is flipped to /client-companies-contracts-management by
 * UC 6cd6e154-ec70-417d-a93f-58f073dffbb4 ("CAF-03: Manage client companies,
 * cafeteria assignment, billing terms, and service calendar") — that page IS
 * the Finance Admin dashboard. `useCaseId` still records the separate,
 * not-yet-built client invoices & billing use case (3f3022a5-...).
 *
 * 'HR Admin' is flipped to /employee-roster-management by
 * UC e7ad1b88-6c4f-4a28-a251-99307eb49815 ("CAF-08: Manage employee roster
 * (CRUD + CSV bulk upload)") — that page IS the HR Admin dashboard.
 * `useCaseId` still records the separate, not-yet-built attendance headcount
 * confirmation use case (e31c6601-...).
 *
 * 'Catering Manager' is flipped to /menu-items-meal-sets-composer by
 * UC 3ce06eb1-dfcf-4d8d-9b83-7a43e6b8ad32 ("CAF-06: Manage menu items and
 * compose meal sets") — that page IS the Catering Manager dashboard.
 *
 * 'Employee' is flipped to /employee-web-credential-qr-page by
 * UC b8e49f1c-5a42-402d-8486-5b0dc31826d0 ("Display dynamic employee HMAC QR
 * code with offline refresh capabilities for meal redemption") — this page
 * IS the Employee's primary (currently only) real destination in the app, so
 * it is a reasonable default landing rather than the generic home page.
 *
 * 'Counter Operator' is flipped to /counter-scan-validation-pos by
 * UC e2a14ded-80b9-454b-8d85-07f5bfa77fcb ("Scan employee QR code, validate
 * active status and meal entitlement cap, and log served meal set") — this
 * page IS the Counter Operator's primary (only) real destination in the app.
 *
 * 'Cafeteria Manager' is flipped to /manager-exception-overrides-void-audit by
 * UC 286a38e6-68eb-4804-ac63-ea84ebd97ce4 ("Review and approve scan exception
 * overrides with mandatory reason logging and manager credentials") — this
 * page IS the Cafeteria Manager's primary (only) real destination in the app,
 * and matches backend/src/modules/rbac/page-registry.js's
 * ROLE_DEFAULT_LANDING['Cafeteria Manager'] (already set to this same slug).
 */
export const ROLE_LANDING_ROUTES = {
  'Operator Admin': {
    path: '/tenant-cafeteria-management',
    useCaseId: '9a125829-3750-461b-a43c-eb8365b4f798', // Manage system users, assign predefined roles, configure RBAC
  },
  'Catering Manager': {
    path: '/menu-items-meal-sets-composer',
    useCaseId: '3ce06eb1-dfcf-4d8d-9b83-7a43e6b8ad32', // Menu items / meal sets composer
  },
  'Cafeteria Manager': {
    path: '/manager-exception-overrides-void-audit',
    useCaseId: '286a38e6-68eb-4804-ac63-ea84ebd97ce4', // Review and approve scan exception overrides
  },
  'Finance Admin': {
    path: '/client-companies-contracts-management',
    useCaseId: '3f3022a5-91dd-4e04-91aa-a95ff9b77671', // Client invoices & billing management
  },
  'HR Admin': {
    path: '/employee-roster-management',
    useCaseId: 'e31c6601-b164-48af-a7da-deaf563a28fc', // Attendance headcount confirmation
  },
  'Counter Operator': {
    path: '/counter-scan-validation-pos',
    useCaseId: 'e2a14ded-80b9-454b-8d85-07f5bfa77fcb', // Counter scan & validation POS
  },
  Employee: {
    path: '/employee-web-credential-qr-page',
    useCaseId: 'b8e49f1c-5a42-402d-8486-5b0dc31826d0', // Employee web credential QR display
  },
};

/**
 * Resolve where a signed-in user should land after authentication.
 *
 * Falls back to `fallbackPath` (typically `location.state?.from?.pathname`
 * captured by a route guard's redirect-to-login, or `/`) whenever the user
 * has no business role, the role isn't in the lookup, or that role's
 * dashboard isn't built yet (`path` still `/`) — never throws, never lands
 * on a route that doesn't exist.
 */
export function resolveLandingPath(businessRole, fallbackPath = '/') {
  const entry = businessRole ? ROLE_LANDING_ROUTES[businessRole] : null;
  return entry?.path || fallbackPath;
}

export default ROLE_LANDING_ROUTES;
